import React, { useMemo, useState, useEffect } from 'react';
import { palette } from '@m12s/component-library';

/**
 * CustomClockRegular - A customizable SVG clock component with auto-play functionality
 *
 * This component renders a circular clock face with a single minute hand that can be positioned
 * manually or animated automatically. It supports pause states and custom styling.
 *
 * @component
 * @example
 * // Basic usage
 * <CustomClockRegular />
 *
 * @example
 * // With custom color and starting position
 * <CustomClockRegular
 *   color="#FF5733"
 *   startMinute={30}
 * />
 *
 * @example
 * // Auto-play configuration
 * <CustomClockRegular
 *   autoPlay={true}
 *   autoPlayMinuteStep={10}
 *   autoPlayMinuteInterval={2000}
 *   startMinute={0}
 * />
 *
 * @example
 * // Paused state
 * <CustomClockRegular
 *   paused={true}
 *   startMinute={45}
 * />
 */
export const CustomClockRegular = ({
  /**
   * The color of the clock face, hands, and pause icon
   * @type {string}
   * @default palette.Grey600
   */
  color = palette.Grey700,

  /**
   * Whether the clock is in a paused state (displays pause icon)
   * @type {boolean}
   * @default false
   */
  paused = false,

  /**
   * The initial minute position (0-59)
   * @type {number}
   * @default 20
   */
  startMinute = 20,

  /**
   * Whether to automatically animate the minute hand
   * @type {boolean}
   * @default false
   */
  autoPlay = false,

  /**
   * The number of minutes to advance on each auto-play step
   * @type {number}
   * @default 5
   */
  autoPlayMinuteStep = 5,

  /**
   * The interval between auto-play steps in milliseconds
   * @type {number}
   * @default 1000
   */
  autoPlayMinuteInterval = 1000,
}) => {
  /**
   * Current minute position (0-59) - updates based on startMinute and autoPlay
   * @type {number}
   */
  const [minute, setMinute] = useState(startMinute);

  /**
   * Effect hook that manages the auto-play functionality and minute initialization
   *
   * - Initializes minute to startMinute value
   * - Sets up auto-play interval when autoPlay is enabled
   * - Cleans up interval on component unmount or dependency changes
   *
   * @effect
   * @dependency {boolean} autoPlay - Enables/disables auto-play
   * @dependency {number} startMinute - Initial minute position
   * @dependency {number} autoPlayMinuteStep - Step size for auto-play
   * @dependency {number} autoPlayMinuteInterval - Interval between steps
   */
  useEffect(() => {
    // Always set the minute to startMinute initially
    setMinute(startMinute);

    let interval;

    // If autoPlay is on, set up interval
    if (autoPlay) {
      interval = setInterval(() => {
        setMinute((prevMinute) => {
          return (prevMinute + autoPlayMinuteStep) % 60;
        });
      }, autoPlayMinuteInterval);
    }

    // Cleanup interval to prevent race conditions
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoPlay, startMinute, autoPlayMinuteStep, autoPlayMinuteInterval]);

  /**
   * Calculated rotation angle for the minute hand in degrees
   * Each minute corresponds to 6 degrees (360° / 60 minutes)
   * @type {number}
   */
  const minuteAngle = useMemo(() => {
    return minute * 6;
  }, [minute]);

  /**
   * Determines the color to use for clock elements based on props
   * Priority: custom color > paused state color > default color
   * @type {string}
   */
  const selectedColor = useMemo(() => {
    if (color) return color;
    if (paused) return palette.Grey600;
    return palette.Grey700;
  }, [paused, color]);

  return (
    /*
     * SVG clock component with the following structure:
     * - Clock face: Circular outline
     * - Minute hand: Rotating path element positioned based on current minute
     * - Pause icon: Conditional dual-rectangle pause symbol (when paused is true)
     *
     * Dimensions: 31x30 viewBox with center at (14, 15.5)
     */
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="31"
      height="30"
      viewBox="0 0 31 30"
      fill="none"
    >
      {/* Clock face - circular outline */}
      <circle
        cx="14"
        cy="15.5"
        r="12.75"
        fill="none"
        stroke={selectedColor}
        strokeWidth="2.5"
      />

      {/* Minute hand - static vertical position */}
      <path
        d="M14 6.72266C13.2344 6.72266 12.6875 7.32422 12.6875 8.03516V15.4727C12.6875 15.9102 12.9062 16.3477 13.2344 16.5664L14 17.083L14.7656 16.5664C15.0938 16.3477 15.3125 15.9102 15.3125 15.4727V8.03516C15.3125 7.32422 14.7109 6.72266 14 6.72266Z"
        fill={selectedColor}
      />

      {/* Minute hand - rotated based on current minute */}
      <path
        d="M14 6.72266C13.2344 6.72266 12.6875 7.32422 12.6875 8.03516V15.4727C12.6875 15.9102 12.9062 16.3477 13.2344 16.5664L14 17.083L14.7656 16.5664C15.0938 16.3477 15.3125 15.9102 15.3125 15.4727V8.03516C15.3125 7.32422 14.7109 6.72266 14 6.72266Z"
        fill={selectedColor}
        transform={`rotate(${minuteAngle}, 14, 15.5)`}
      />

      {/* Pause icon - displayed when paused is true */}
      {paused && (
        <>
          {/* White background for pause icon */}
          <rect fill="white" x="20" y="0" height="13" width="12" />

          {/* Dual pause bars */}
          <path
            d="M22.5 0.5H23.5C24.3125 0.5 25 1.1875 25 2V11C25 11.8438 24.3125 12.5 23.5 12.5H22.5C21.6562 12.5 21 11.8438 21 11V2C21 1.1875 21.6562 0.5 22.5 0.5ZM28.5 0.5H29.5C30.3125 0.5 31 1.1875 31 2V11C31 11.8438 30.3125 12.5 29.5 12.5H28.5C27.6562 12.5 27 11.8438 27 11V2C27 1.1875 27.6562 0.5 28.5 0.5Z"
            fill={selectedColor}
          />
        </>
      )}
    </svg>
  );
};
